import { useState, useEffect, useRef, useMemo } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, getCardClass, hexToRgba, resolveLinkProps, resolveVideo } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

// Browser chrome mockup wrapper
function BrowserMockup({ children, accent, dark, title }) {
    return (
        <div className="rounded-xl overflow-hidden shadow-2xl" style={{ border: `1px solid ${hexToRgba(accent, 0.2)}` }}>
            {/* Top bar */}
            <div
                className="h-9 flex items-center px-4 gap-3 shrink-0"
                style={{ backgroundColor: dark ? '#1e1e2e' : '#f1f3f4', borderBottom: `1px solid ${hexToRgba(accent, 0.12)}` }}
            >
                {/* Traffic light dots */}
                <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#ff5f56' }} />
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#ffbd2e' }} />
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#27c93f' }} />
                </div>
                {/* Fake address bar */}
                <div
                    className="flex-1 h-5 rounded-full flex items-center px-3"
                    style={{ backgroundColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}
                >
                    <svg className="w-3 h-3 mr-2 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    {title && (
                        <span className="text-[11px] truncate opacity-50">{title}</span>
                    )}
                </div>
            </div>
            {/* Content */}
            <div className="relative">{children}</div>
        </div>
    );
}

// Placeholder shown when no URL is provided
function DemoPlaceholder({ accent, dark, cta_text, cta_link, s }) {
    return (
        <div
            className="aspect-video flex flex-col items-center justify-center gap-6 text-center px-8"
            style={{ backgroundColor: dark ? '#111827' : hexToRgba(accent, 0.04) }}
        >
            <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: hexToRgba(accent, 0.12) }}
            >
                <svg className="w-10 h-10" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
            </div>
            <div>
                <p className="text-base font-semibold mb-1" style={{ color: s.text }}>Demo interactiva</p>
                <p className="text-sm" style={{ color: s.textSub }}>Agrega una URL de demo o video para mostrar aqui</p>
            </div>
            {cta_text && cta_link && (
                <a
                    {...resolveLinkProps(cta_link)}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
                    style={{ backgroundColor: accent, boxShadow: `0 4px 16px ${hexToRgba(accent, 0.3)}` }}
                >
                    {cta_text}
                </a>
            )}
        </div>
    );
}

// Feature highlight card
function FeatureCard({ feature, accent, s, dark, delay, visible, card }) {
    return (
        <div
            className={`${card} p-5 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
        >
            {feature.icon && (
                <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 text-xl"
                    style={{ backgroundColor: hexToRgba(accent, 0.1) }}
                >
                    {feature.icon}
                </div>
            )}
            <h4 className="font-bold text-sm mb-1.5" style={{ color: s.text }}>{feature.title}</h4>
            {feature.description && (
                <p className="text-xs leading-relaxed" style={{ color: s.textSub }}>{feature.description}</p>
            )}
        </div>
    );
}

export default function ProductDemoSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const variant = config.variant || 'centered';

    const features = content.features || [];
    const video = resolveVideo(content, 'video', mediaFolders);
    const demoVideo = resolveVideo({ video_url: content.demo_url }, 'video', {});
    const embedUrl = demoVideo?.embed || video?.embed || null;
    const isFileVideo = video?.type === 'file';
    const isVideo = !content.demo_url && (!!content.video_url || isFileVideo);
    const hasEmbed = !!embedUrl || isFileVideo;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const columns = config.feature_columns || (variant === 'split' ? 1 : features.length <= 3 ? features.length : 3);
    const colClass = columns === 1 ? '' : columns === 2 ? 'md:grid-cols-2' : columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3';

    // ── Demo/video block ──
    const DemoBlock = ({ className = '' }) => (
        <div className={className}>
            <BrowserMockup accent={s.accent} dark={s.dark} title={content.demo_url || content.video_url || ''}>
                {hasEmbed ? (
                    <div className="aspect-video">
                        {isFileVideo ? (
                            <video src={video.src} className="w-full h-full" controls preload="metadata" />
                        ) : isVideo ? (
                            <iframe
                                src={`${embedUrl}?controls=1&playsinline=1&rel=0`}
                                className="w-full h-full"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title={content.title || 'Demo video'}
                            />
                        ) : (
                            <iframe
                                src={embedUrl}
                                className="w-full h-full"
                                frameBorder="0"
                                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                                title={content.title || 'Product demo'}
                            />
                        )}
                    </div>
                ) : (
                    <DemoPlaceholder
                        accent={s.accent}
                        dark={s.dark}
                        cta_text={content.cta_text}
                        cta_link={content.cta_link}
                        s={s}
                    />
                )}
            </BrowserMockup>
        </div>
    );

    // ── CTA button ──
    const CtaBtn = () => content.cta_text && content.cta_link ? (
        <div className="mt-8 flex justify-center">
            <a
                {...resolveLinkProps(content.cta_link)}
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
                style={{ backgroundColor: s.accent, boxShadow: `0 4px 20px ${hexToRgba(s.accent, 0.35)}` }}
            >
                {content.cta_text}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </a>
        </div>
    ) : null;

    // ── FULLWIDTH variant ──
    if (variant === 'fullwidth') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <DemoBlock className="w-full" />
                    {features.length > 0 && (
                        <div className={`grid grid-cols-1 ${colClass} gap-4 mt-10`}>
                            {features.map((f, i) => (
                                <FeatureCard key={i} feature={f} accent={s.accent} s={s} dark={s.dark} delay={i * 80} visible={visible} card={card} />
                            ))}
                        </div>
                    )}
                    <CtaBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── SPLIT variant (demo left, features right) ──
    if (variant === 'split') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                        {/* Left: demo */}
                        <DemoBlock />
                        {/* Right: features + CTA */}
                        <div className="flex flex-col gap-4">
                            {content.description && (
                                <p className="text-base leading-relaxed mb-2" style={{ color: s.textSub }}>{content.description}</p>
                            )}
                            {features.map((f, i) => (
                                <div
                                    key={i}
                                    className={`flex items-start gap-4 p-4 rounded-xl transition-all duration-500 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}
                                    style={{
                                        transitionDelay: visible ? `${i * 100}ms` : '0ms',
                                        backgroundColor: s.dark ? hexToRgba('#ffffff', 0.05) : hexToRgba(s.accent, 0.04),
                                        border: `1px solid ${hexToRgba(s.accent, 0.1)}`,
                                    }}
                                >
                                    {f.icon && (
                                        <div
                                            className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0"
                                            style={{ backgroundColor: hexToRgba(s.accent, 0.12) }}
                                        >
                                            {f.icon}
                                        </div>
                                    )}
                                    <div>
                                        <h4 className="font-bold text-sm mb-0.5" style={{ color: s.text }}>{f.title}</h4>
                                        {f.description && <p className="text-xs leading-relaxed" style={{ color: s.textSub }}>{f.description}</p>}
                                    </div>
                                </div>
                            ))}
                            {content.cta_text && content.cta_link && (
                                <div className="mt-4">
                                    <a
                                        {...resolveLinkProps(content.cta_link)}
                                        className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold text-sm text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
                                        style={{ backgroundColor: s.accent, boxShadow: `0 4px 16px ${hexToRgba(s.accent, 0.35)}` }}
                                    >
                                        {content.cta_text}
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── CENTERED (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                {content.description && (
                    <p className="text-center text-base max-w-2xl mx-auto mb-8 leading-relaxed" style={{ color: s.textSub }}>
                        {content.description}
                    </p>
                )}

                <DemoBlock className="max-w-4xl mx-auto" />

                {features.length > 0 && (
                    <div className={`grid grid-cols-1 ${colClass} gap-4 mt-10`}>
                        {features.map((f, i) => (
                            <FeatureCard key={i} feature={f} accent={s.accent} s={s} dark={s.dark} delay={i * 80} visible={visible} card={card} />
                        ))}
                    </div>
                )}

                <CtaBtn />
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
